import React, { Suspense, useCallback, useContext, useEffect, useState } from "react";
import toast from "react-hot-toast";
import LsLoading from "Components/LsLoading";
import { ModuleFooter as Footer } from "Components/Layout";
import { Header } from "Components/Layout";
import { NewMessageHandler, useCommonHub } from "Utils/CommonHub";
// import { Hint } from "EnterprisePortal/Components/Hint";
import { Banner } from "../../Banner";
import PortalNav, { PortalNavProps } from "../../PortalNav";
import LsToaster from "../../Toaster";
import NewMessageToast from "../../NewMessageToast";
import AccountContext from "../../../AccountContext";
import classNames from "classnames";

interface IPortalLayoutContext {
   setFooterVisible: (isVisible: boolean) => void,
   setNavigationVariant: (variant: PortalNavProps["variant"]) => void,
   setMainClass: (className: string | undefined) => void,
}

const PortalLayoutContext = React.createContext<IPortalLayoutContext>({
   setFooterVisible() { /*void*/ },
   setNavigationVariant() { /*void*/ },
   setMainClass() { /*void*/ }
});
const TitleContext = React.createContext<{ setTitle: (title: string) => void }>({ setTitle() { /*void*/ } });

export function useNavigationVariant(variant: PortalNavProps["variant"]) {
   const { setNavigationVariant } = useContext(PortalLayoutContext);
   useEffect(() => {
      setNavigationVariant(variant);
      return () => {
         setNavigationVariant(undefined);
      }
   }, [variant]);
}

export function useMainClass(className: string | undefined) {
   const { setMainClass } = useContext(PortalLayoutContext);
   useEffect(() => {
      setMainClass(className);
      return () => {
         setMainClass(undefined);
      }
   }, [className]);
}

export function useFooterVisible(isVisible: boolean) {   
   const { setFooterVisible } = useContext(PortalLayoutContext);
   useEffect(() => {
      setFooterVisible(isVisible);

      return function cleanup() {
         setFooterVisible(true);
      }
   }, [isVisible]);
}

export function useTitle(title: string) {
   const { setTitle } = useContext(TitleContext);
   useEffect(() => {
      setTitle(title);
   }, [title])
}

export const Main: React.FC = function ({ children }) {
   /*let { path, url } = useRouteMatch();*/
   const account = useContext(AccountContext);

   function setTitle(title: string) {
      document.title = `${account.name} | ${title}`;
   }

   const newMessageHandler: NewMessageHandler = useCallback(data => {
      toast.custom(toastInstance => <NewMessageToast data={data} toastInstance={toastInstance} />);
   }, []);
   useCommonHub({
      onNewMessage: newMessageHandler
   });

   const [isFooterVisible, setFooterVisible] = useState(true);
   const [navigationVariant, setNavigationVariant] = useState<PortalNavProps["variant"]>();
   const [mainClass, setMainClass] = useState<string>();

   return (
      <PortalLayoutContext.Provider value={{ setFooterVisible, setNavigationVariant, setMainClass }}>

         <div id="header-wrapper" className="header-wrapper portal-header-wrapper" data-design="marketing">
            <Header fluid noSearch />
         </div>
         <div className={classNames("portal-left", { "portal-left-minimized": navigationVariant == "minimized" })}>
            <PortalNav linkType={"route"} variant={navigationVariant} />
            {/* <Hint className="mt-px24" /> */}
         </div>
         <div className={classNames("portal-right", { "portal-right-minimized": navigationVariant == "minimized" })}>
            <div className="portal-banner-wrapper">
               <Banner accountId={account.id} />
            </div>
            <main className={classNames("portal-main", mainClass)}>
               <TitleContext.Provider value={{ setTitle }}>
                  <Suspense fallback={<LsLoading isLoading={true} />}>
                     {children}
                  </Suspense>
               </TitleContext.Provider>
            </main>
            {
               isFooterVisible
                  ? <div className="portal-footer-wrapper">
                     <Footer />
                  </div>
                  : null
            }
         </div>
         <LsToaster />
      </PortalLayoutContext.Provider>
   );
}
